import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { ConfirmModal, MoreMenu, StateView } from '@/card/record'
import { Icon } from '@/components/Icon'
import {
  fetchLedger,
  fetchLedgerBalance,
  fetchLedgerTotals,
  recordManualEntry,
  reverseLedgerEntry,
} from './api'
import {
  amountOf,
  dateText,
  Failure,
  Money,
  OrganizationPicker,
  OrganizationRequired,
  OrganizationsFailure,
  PaymentsPage,
  QueryFailure,
  TableSkeleton,
  timeText,
  useOrganizations,
} from './PaymentsShell'
import type { LedgerEntry, LedgerTotal, Period } from './types'

/**
 * Кассовая книга: приход, расход, сальдо (ТЗ §4.4) — тикет #78, вёрстка по Cashbook.dc.html.
 *
 * <h2>Без организации денег нет</h2>
 *
 * Пока юрлицо не выбрано, экран не показывает ни одной суммы и не делает ни одного
 * запроса к журналу: у каждой организации своя касса, и «общий остаток» двух юрлиц —
 * число, которого нет ни в одной бумаге.
 *
 * <h2>Почему исправление — «сторнировать», а не «править»</h2>
 *
 * Журнал append-only: UPDATE и DELETE отозваны у роли приложения и запрещены триггером на
 * каждой партиции (V17/V18). Исправление — это **вторая** запись, обратная первой, и обе
 * остаются видны. Поэтому сторно — в меню «⋯» строки с подтверждением и обязательной
 * причиной: обратимым его не назвать, отменяется оно только ещё одной записью.
 *
 * <h2>Почему сальдо приходит с сервера</h2>
 *
 * Остаток — свойство периода, а не экрана: посчитанный из видимых строк, он разойдётся с
 * серверным, как только список станет постраничным. Приход и расход складываются из
 * оборотов по статьям — тоже серверных.
 */
type Mode = 'day' | 'month'

const INCOME_ITEMS = ['sales_revenue', 'customer_advance', 'other_income'] as const
const EXPENSE_ITEMS = ['refund_issued', 'supplier_payment', 'payroll', 'taxes', 'marketing', 'rent', 'other_expense'] as const

export function CashBookPage() {
  const { t, user } = useApp()
  const [organizationId, setOrganizationId] = useState<string | null>(null)
  const [mode, setMode] = useState<Mode>('day')
  const [anchor, setAnchor] = useState(() => isoDate(new Date()))
  const [reversing, setReversing] = useState<LedgerEntry | null>(null)
  const [reason, setReason] = useState('')
  const [manual, setManual] = useState<'incoming' | 'outgoing' | null>(null)
  const queryClient = useQueryClient()

  const organizations = useOrganizations()
  const organization = (organizations.data ?? []).find((candidate) => candidate.id === organizationId) ?? null
  // Валюта кассы — базовая валюта юрлица, а не рубль по умолчанию: касса в тенге,
  // показанная в рублях, — это ноль, который выглядит правдой.
  const currency = organization?.baseCurrency ?? 'RUB'
  const period = periodOf(mode, anchor)
  const enabled = Boolean(organizationId)

  // Сторно и ручная проводка — право `create` журнала: журнал append-only, `update` в нём
  // нет ни у кого.
  const mayWrite = can(user, 'payments.ledger', 'create')

  const entries = useQuery({
    queryKey: ['cash-ledger', organizationId, period],
    queryFn: ({ signal }) => fetchLedger(organizationId!, period, signal),
    enabled,
    retry: false,
  })

  const totals = useQuery({
    queryKey: ['cash-ledger-totals', organizationId, currency, period],
    queryFn: ({ signal }) => fetchLedgerTotals(organizationId!, currency, period, signal),
    enabled,
    retry: false,
  })

  const balance = useQuery({
    queryKey: ['cash-ledger-balance', organizationId, currency, period],
    queryFn: ({ signal }) => fetchLedgerBalance(organizationId!, currency, period, signal),
    enabled,
    retry: false,
  })

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['cash-ledger'] })
    void queryClient.invalidateQueries({ queryKey: ['cash-ledger-totals'] })
    void queryClient.invalidateQueries({ queryKey: ['cash-ledger-balance'] })
  }

  const reverse = useMutation({
    mutationFn: ({ id, why }: { id: number; why: string }) => reverseLedgerEntry(id, why),
    onSuccess: () => {
      setReversing(null)
      setReason('')
      invalidate()
    },
  })

  const record = useMutation({
    mutationFn: recordManualEntry,
    onSuccess: () => {
      setManual(null)
      invalidate()
    },
  })

  const incoming = (totals.data ?? []).filter((total) => total.direction === 'incoming')
  const outgoing = (totals.data ?? []).filter((total) => total.direction === 'outgoing')
  const sumIn = incoming.reduce((sum, total) => sum + amountOf(total.total), 0)
  const sumOut = outgoing.reduce((sum, total) => sum + amountOf(total.total), 0)
  const periodText = mode === 'day' ? dateText(anchor) : monthText(anchor)
  const failure = entries.error ?? totals.error ?? balance.error
  const loading = entries.isLoading || totals.isLoading || balance.isLoading
  const today = isoDate(new Date())

  const actions = organizationId && mayWrite ? (
    <>
      <button type="button" className="btn btn-secondary" onClick={() => setManual('outgoing')}>
        <Icon name="arrow-up" />
        {t('payments.cash.newOutgoing')}
      </button>
      <button type="button" className="btn btn-primary" onClick={() => setManual('incoming')}>
        <Icon name="plus" />
        {t('payments.cash.newIncoming')}
      </button>
    </>
  ) : null

  return (
    <PaymentsPage
      titleKey="nav.cashBook"
      head={<OrganizationPicker value={organizationId} onChange={setOrganizationId} />}
      actions={actions}
    >
      {organizations.error ? (
        <OrganizationsFailure error={organizations.error} onRetry={() => void organizations.refetch()} />
      ) : organizations.isLoading ? (
        <TableSkeleton columns={tableColumns(t)} />
      ) : !organizationId ? (
        <OrganizationRequired
          organizations={organizations.data ?? []}
          onPick={setOrganizationId}
          titleKey="payments.cash.noOrg.title"
          textKey="payments.cash.noOrg.text"
        />
      ) : (
        <>
          <div className="toolbar">
            <div className="row" style={{ gap: 4 }}>
              <button
                type="button"
                className="btn btn-ghost btn-sm btn-icon"
                aria-label={t(mode === 'day' ? 'payments.cash.prevDay' : 'payments.cash.prevMonth')}
                onClick={() => setAnchor(shift(anchor, mode, -1))}
              >
                <Icon name="arrow-left" size="sm" />
              </button>
              <input
                type="date"
                className="input input-sm pay-date-input"
                aria-label={t('payments.cash.date')}
                value={anchor}
                max={today}
                onChange={(event) => event.target.value && setAnchor(event.target.value)}
              />
              <button
                type="button"
                className="btn btn-ghost btn-sm btn-icon"
                aria-label={t(mode === 'day' ? 'payments.cash.nextDay' : 'payments.cash.nextMonth')}
                // В будущем проводок нет — шаг туда показал бы пустоту, похожую на правду.
                disabled={periodOf(mode, shift(anchor, mode, 1)).from > today}
                onClick={() => setAnchor(shift(anchor, mode, 1))}
              >
                <Icon name="arrow-right" size="sm" />
              </button>
            </div>
            <div className="seg" role="group" aria-label={t('payments.cash.period')}>
              <button type="button" aria-pressed={mode === 'day'} onClick={() => setMode('day')}>
                {t('payments.cash.day')}
              </button>
              <button type="button" aria-pressed={mode === 'month'} onClick={() => setMode('month')}>
                {t('payments.cash.month')}
              </button>
            </div>
            <span className="spacer" />
            <span className="t-sm muted">{t('payments.cash.currency', { currency })}</span>
          </div>

          {reverse.error ? <Failure error={reverse.error} /> : null}

          {failure ? (
            <QueryFailure
              error={failure}
              onRetry={() => {
                void entries.refetch()
                void totals.refetch()
                void balance.refetch()
              }}
            />
          ) : loading ? (
            <TableSkeleton columns={tableColumns(t)} />
          ) : (
            <>
              <div className="facts" aria-label={t('payments.cash.facts', { period: periodText })}>
                <div className="fact">
                  <span className="k">{t('payments.cash.in')}</span>
                  <span className="v pay-in"><Money value={sumIn} currency={currency} /></span>
                  <span className="s">{t('payments.cash.items', { count: incoming.length })}</span>
                </div>
                <div className="fact">
                  <span className="k">{t('payments.cash.out')}</span>
                  <span className="v"><Money value={sumOut} currency={currency} /></span>
                  <span className="s">{t('payments.cash.items', { count: outgoing.length })}</span>
                </div>
                <div className="fact">
                  <span className="k">{t('payments.cash.net')}</span>
                  <span className="v"><Money value={balance.data?.netFlow} currency={currency} /></span>
                  <span className="s">{t('payments.cash.net.hint', { period: periodText })}</span>
                </div>
              </div>

              <div className="layout-2">
                <div className="stack" style={{ gap: 10, minWidth: 0 }}>
                  {(entries.data ?? []).length === 0 ? (
                    <StateView
                      boxed
                      kind="empty"
                      title={t('payments.cash.empty.title', { period: periodText })}
                      description={t('payments.cash.empty.text')}
                    />
                  ) : (
                    <EntriesTable
                      entries={entries.data ?? []}
                      periodText={periodText}
                      mayWrite={mayWrite}
                      onReverse={(entry) => {
                        setReason('')
                        setReversing(entry)
                      }}
                    />
                  )}
                </div>

                <div className="stack" style={{ gap: 14 }}>
                  <ItemsPanel titleKey="payments.cash.byItemIn" totals={incoming} sum={sumIn}
                              currency={currency} emptyKey="payments.cash.noIn" income />
                  <ItemsPanel titleKey="payments.cash.byItemOut" totals={outgoing} sum={sumOut}
                              currency={currency} emptyKey="payments.cash.noOut" />
                  <section className="panel" aria-labelledby="cash-org-h">
                    <div className="panel-h"><span className="h2" id="cash-org-h">{t('payments.cash.org')}</span></div>
                    <div className="panel-b">
                      <dl className="kv">
                        <dt>{t('payments.organization')}</dt>
                        <dd>{organization?.legalName ?? organization?.name}</dd>
                        <dt>{t('column.inn')}</dt>
                        <dd className="mono">{organization?.inn}{organization?.kpp ? ` / ${organization.kpp}` : ''}</dd>
                        <dt>{t('payments.cash.currencyLabel')}</dt>
                        <dd>{currency}</dd>
                      </dl>
                    </div>
                  </section>
                </div>
              </div>
            </>
          )}
        </>
      )}

      <ConfirmModal
        open={reversing != null}
        tone="warning"
        icon="refresh"
        title={t('payments.cash.reverse.title', { id: reversing?.id ?? '' })}
        confirmLabel={t('payments.reverse')}
        pending={reverse.isPending}
        // Причина обязательна — её требует сервер. Кнопка без неё не нажимается: отказ 422
        // на кнопку, которая выглядела рабочей, учит не верить кнопкам.
        confirmDisabled={reason.trim() === ''}
        onCancel={() => setReversing(null)}
        onConfirm={() => reversing && reverse.mutate({ id: reversing.id, why: reason.trim() })}
      >
        <p>{t('payments.cash.reverse.text')}</p>
        <div className="field pay-form">
          <label className="label" htmlFor="cash-reverse-reason">
            {t('payments.reverse.reason')} <span className="req" aria-hidden="true">*</span>
          </label>
          <textarea
            id="cash-reverse-reason"
            className="input"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      </ConfirmModal>

      {manual && organizationId && (
        <ManualEntryDialog
          direction={manual}
          organizationId={organizationId}
          currency={currency}
          pending={record.isPending}
          error={record.error}
          onCancel={() => {
            setManual(null)
            record.reset()
          }}
          onSubmit={(entry) => record.mutate(entry)}
        />
      )}
    </PaymentsPage>
  )
}

function tableColumns(t: (key: string) => string): string[] {
  return [
    t('payments.cash.col.date'),
    t('payments.cash.col.kind'),
    t('payments.cash.col.item'),
    t('payments.cash.col.in'),
    t('payments.cash.col.out'),
    '',
  ]
}

function EntriesTable({ entries, periodText, mayWrite, onReverse }: {
  entries: LedgerEntry[]
  periodText: string
  mayWrite: boolean
  onReverse: (entry: LedgerEntry) => void
}) {
  const { t } = useApp()
  // Строки печатаются в своей валюте; итог строкой — только когда валюта одна.
  const currencies = new Set(entries.map((entry) => entry.currency))
  const single = currencies.size === 1 ? entries[0]?.currency : null
  const sumIn = entries.filter((entry) => entry.direction === 'incoming').reduce((sum, entry) => sum + amountOf(entry.amount), 0)
  const sumOut = entries.filter((entry) => entry.direction === 'outgoing').reduce((sum, entry) => sum + amountOf(entry.amount), 0)
  const reversed = new Set(entries.map((entry) => entry.reversesEntryId).filter((id) => id != null))

  return (
    <div className="tbl-wrap">
      <table className="tbl">
        <thead>
          <tr>
            <th>{t('payments.cash.col.date')}</th>
            <th>{t('payments.cash.col.kind')}</th>
            <th>{t('payments.cash.col.item')}</th>
            <th className="num">{t('payments.cash.col.in')}</th>
            <th className="num">{t('payments.cash.col.out')}</th>
            <th><span className="sr-only">{t('payments.recon.col.action')}</span></th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id} className={entry.reversal || reversed.has(entry.id) ? 'is-muted' : undefined}>
              <td>
                <span className="stack">
                  <span className="mono">№ {entry.id}</span>
                  <span className="t-xs muted">{dateText(entry.entryDate)} {timeText(entry.entryDate)}</span>
                </span>
              </td>
              <td>
                {entry.direction === 'incoming' ? (
                  <span className="badge b-success"><Icon name="arrow-down" />{t('payments.direction.incoming')}</span>
                ) : (
                  <span className="badge"><Icon name="arrow-up" />{t('payments.direction.outgoing')}</span>
                )}
              </td>
              <td className="wrap" style={{ minWidth: 180 }}>
                <span className="stack">
                  <span>{t(`payments.item.${entry.item}`)}</span>
                  <span className="t-xs muted">
                    {entry.description ?? '—'}
                    {entry.reversal && (
                      <> · {t('payments.cashBook.reverses', { id: entry.reversesEntryId ?? '' })}</>
                    )}
                    {reversed.has(entry.id) && <> · {t('payments.cash.reversedMark')}</>}
                  </span>
                </span>
              </td>
              <td className="num">
                {entry.direction === 'incoming' ? <Money value={entry.amount} currency={entry.currency} /> : ''}
              </td>
              <td className="num">
                {entry.direction === 'outgoing' ? <Money value={entry.amount} currency={entry.currency} /> : ''}
              </td>
              <td className="num">
                {/* Сторно самого сторно не предлагается: у ошибки одно исправление, и сервер
                    такую попытку отвергает. Кнопка, которая заведомо получит отказ, — это
                    обещание, которого нет. */}
                {mayWrite && !entry.reversal && !reversed.has(entry.id) && (
                  <MoreMenu
                    compact
                    label={t('payments.cash.rowMenu', { id: entry.id })}
                    items={[{ id: 'reverse', label: t('payments.reverse'), danger: true, onSelect: () => onReverse(entry) }]}
                  />
                )}
              </td>
            </tr>
          ))}
          <tr className="total">
            <td colSpan={3}>{t('payments.cash.total', { period: periodText, count: entries.length })}</td>
            <td className="num">{single ? <Money value={sumIn} currency={single} /> : '—'}</td>
            <td className="num">{single ? <Money value={sumOut} currency={single} /> : '—'}</td>
            <td />
          </tr>
        </tbody>
      </table>
    </div>
  )
}

function ItemsPanel({ titleKey, totals, sum, currency, emptyKey, income }: {
  titleKey: string
  totals: LedgerTotal[]
  sum: number
  currency: string
  emptyKey: string
  income?: boolean
}) {
  const { t } = useApp()
  const max = Math.max(...totals.map((total) => amountOf(total.total)), 0)
  return (
    <section className="panel" aria-label={t(titleKey)}>
      <div className="panel-h">
        <span className="h2">{t(titleKey)}</span>
        <span className="spacer" />
        <span className="strong t-sm"><Money value={sum} currency={currency} /></span>
      </div>
      <div className="panel-b pay-panel-list">
        {totals.length === 0 && <span className="secondary t-sm">{t(emptyKey)}</span>}
        {totals.map((total) => {
          const value = amountOf(total.total)
          const share = sum > 0 ? Math.round((value / sum) * 100) : 0
          return (
            <div key={total.item} className="stack" style={{ gap: 4 }}>
              <div className="pay-panel-row">
                <span>{total.itemName ?? t(`payments.item.${total.item}`)}</span>
                <span className="num">
                  <Money value={total.total} currency={currency} /> <span className="muted t-xs">· {share} %</span>
                </span>
              </div>
              <div className="hbar-track" aria-hidden="true">
                <div className={income ? 'hbar hbar-in' : 'hbar'} style={{ width: `${max > 0 ? (value / max) * 100 : 0}%` }} />
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

/**
 * Ручная проводка: приход или расход кассы без платежа.
 *
 * Это не ПКО/РКО со своей нумерацией и кассиром — такого документа в системе нет, — а
 * запись журнала со статьёй и обязательным описанием. Подпись кнопки поэтому «Приход в
 * кассу», а не «Новый ПКО»: обещать документ, которого не будет, хуже, чем назвать то,
 * что есть.
 */
function ManualEntryDialog({ direction, organizationId, currency, pending, error, onCancel, onSubmit }: {
  direction: 'incoming' | 'outgoing'
  organizationId: string
  currency: string
  pending: boolean
  error: unknown
  onCancel: () => void
  onSubmit: (entry: Parameters<typeof recordManualEntry>[0]) => void
}) {
  const { t } = useApp()
  const items = direction === 'incoming' ? INCOME_ITEMS : EXPENSE_ITEMS
  const [date, setDate] = useState(() => isoDate(new Date()))
  const [item, setItem] = useState<string>(items[0])
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const normalized = amount.replace(/\s/g, '').replace(',', '.')
  const valid = /^\d+(\.\d{1,2})?$/.test(normalized) && Number(normalized) > 0 && description.trim() !== ''

  return (
    <div className="modal-layer" onKeyDown={(event) => event.key === 'Escape' && onCancel()}>
      <form
        className="modal modal-lg"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cash-manual-title"
        onSubmit={(event) => {
          event.preventDefault()
          if (!valid) return
          onSubmit({
            organizationId,
            date,
            item,
            direction,
            amount: normalized,
            currency,
            description: description.trim(),
          })
        }}
      >
        <div className="modal-h is-form">
          <span id="cash-manual-title" className="h2" style={{ flex: 1, fontSize: 15 }}>
            {t(direction === 'incoming' ? 'payments.cash.newIncoming' : 'payments.cash.newOutgoing')}
          </span>
          <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('card.cancel')} onClick={onCancel}>
            <Icon name="close" size="sm" />
          </button>
        </div>
        <div className="modal-b is-form">
          {error ? <Failure error={error} /> : null}
          <div className="form-grid">
            <div className="field">
              <label className="label" htmlFor="cash-manual-date">{t('payments.cash.col.date')}</label>
              <input id="cash-manual-date" type="date" className="input" value={date}
                     onChange={(event) => setDate(event.target.value)} required />
            </div>
            <div className="field">
              <label className="label" htmlFor="cash-manual-amount">
                {t('payments.column.paymentAmount')}, {currency} <span className="req" aria-hidden="true">*</span>
              </label>
              <input id="cash-manual-amount" className="input" inputMode="decimal" value={amount}
                     onChange={(event) => setAmount(event.target.value)} required />
            </div>
            <div className="field span-2">
              <label className="label" htmlFor="cash-manual-item">{t('payments.column.cashItem')}</label>
              <select id="cash-manual-item" className="input" value={item} onChange={(event) => setItem(event.target.value)}>
                {items.map((code) => (
                  <option key={code} value={code}>{t(`payments.item.${code}`)}</option>
                ))}
              </select>
            </div>
            <div className="field span-2">
              <label className="label" htmlFor="cash-manual-description">
                {t('payments.cash.basis')} <span className="req" aria-hidden="true">*</span>
              </label>
              <textarea id="cash-manual-description" className="input" value={description}
                        onChange={(event) => setDescription(event.target.value)} required />
              <span className="help">{t('payments.cash.basis.help')}</span>
            </div>
          </div>
        </div>
        <div className="modal-f">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>{t('card.cancel')}</button>
          <button type="submit" className="btn btn-primary" disabled={!valid || pending}>
            {pending ? t('card.saving') : t('payments.cash.record')}
          </button>
        </div>
      </form>
    </div>
  )
}

function isoDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** День — сам этот день; месяц — с первого числа по последнее (или по сегодня). */
function periodOf(mode: Mode, anchor: string): Period {
  if (mode === 'day') return { from: anchor, to: anchor }
  const [y, m] = anchor.split('-').map(Number) as [number, number]
  const first = new Date(y, m - 1, 1)
  const last = new Date(y, m, 0)
  return { from: isoDate(first), to: isoDate(last) }
}

function shift(anchor: string, mode: Mode, step: number): string {
  const [y, m, d] = anchor.split('-').map(Number) as [number, number, number]
  if (mode === 'day') return isoDate(new Date(y, m - 1, d + step))
  return isoDate(new Date(y, m - 1 + step, 1))
}

function monthText(anchor: string): string {
  const [y, m] = anchor.split('-')
  return `${m}.${y}`
}
